import {
  Play,
  Player,
  getAvatarSrc,
  getPackNameById,
  GameResultTitle,
  t,
  Storage,
} from '@whitesharx/starfall-frontend-core';
import type { Api, Key, MoveGrade } from '@whitesharx/starfall-frontend-chessground';
import { AnalyzedMoveResponse, MoveScoreResponse } from './requests/analyze';
import { EvaluatedMove, ProcessedAnalyzedMove, BestMove, AnalysisGame, Eval, ContextfullAnalyzedMove } from './types';
import { ANALYZED_MOVES_KEY } from './constants';

export const group = <T>(arr: T[], size: number) => {
  const copy = [...arr];
  const result = [];
  const count = Math.ceil(arr.length / size);

  for (let index = 0; index < count; index++) {
    result.push(copy.slice(index * size, index * size + size));
  }

  return result;
};

const evaluateCp = (cp: number) => Number((((cp / 100 + Number.EPSILON) * 10) / 10).toFixed(2));

const toPov = (value: number, color: 'w' | 'b') => (color === 'w' ? value : value * -1);

const mateToCp = (mate: number) => {
  return mate > 0 ? 1000 : -1000;
};

export const evaluateScore = (score: MoveScoreResponse, color: 'w' | 'b') => {
  const value = 'cp' in score ? Number(score.cp) : Number(score.mate);

  return 'cp' in score ? toPov(evaluateCp(value), color) : toPov(evaluateCp(mateToCp(value)), color);
};

export const parseEval = (move: AnalyzedMoveResponse, color: 'w' | 'b'): Eval => {
  if ('cp' in move.score) {
    return {
      cp: toPov(Number(move.score.cp), color),
      mate: undefined,
    };
  }

  return {
    cp: undefined,
    mate: toPov(Number(move.score.mate), color),
  };
};

export const gradeMove = (move: EvaluatedMove, color: 'w' | 'b', best?: BestMove): MoveGrade => {
  const checkmate = move.san.endsWith('#');
  if (move.move === best?.fullMove || checkmate) {
    return 'best';
  }

  if (Math.abs(best?.eval?.mate || 0) === 1) {
    return 'missed-win';
  }

  const oldScore = best?.score || 0;
  const diff = move.score - oldScore;
  const badGrade = color === 'w' ? diff < 0 : diff > 0;

  if (badGrade) {
    const abs = Math.abs(diff);

    if (abs >= 5) {
      return 'blunder';
    }

    if (abs >= 2) {
      return 'mistake';
    }

    if (abs > 0.5) {
      return 'slip';
    }

    return 'good';
  }

  if (diff >= 2) {
    return 'great';
  }

  if (diff >= 0.5) {
    return 'good';
  }

  return 'good';
};

export const formatePlayerData = (player: Player | null, figuresSetNameId?: string) => {
  if (!player) {
    return null;
  }

  return {
    id: player.id,
    name: player.name,
    country: player.flagIcon || player.countryCode,
    avatarSrc: getAvatarSrc(player.imageUrl),
    setName: getPackNameById(player.productId),
    figuresSetName: getPackNameById(figuresSetNameId || player.productId),
  };
};

export const formatEval = (evaluation?: Eval) => {
  if (!evaluation) {
    return '';
  }

  if (evaluation.cp !== undefined) {
    const score = evaluation.cp / 100;
    const sign = score > 0 ? '+' : '';
    return score !== 0 ? `${sign}${score.toFixed(2)}` : '0.00';
  }

  const sign = evaluation.mate > 0 ? '+' : '-';
  const mate = Math.abs(evaluation.mate);
  return `${sign}M${mate}`;
};

export const getSummary = (game: AnalysisGame, resultTitle: GameResultTitle, playerId: string) => {
  if (resultTitle === GameResultTitle.canceled) {
    const isWhite = game.whitePlayerId === playerId;
    const playerMoved = game.moves[0]?.playerId === playerId;
    return {
      title: t('analysis.summary.game-canceled'),
      desc:
        isWhite && playerMoved
          ? t('analysis.summary.game-canceled-your-opponent')
          : t('analysis.summary.game-canceled-you'),
    };
  }

  if (!game.moves.length) {
    return {
      title: t('analysis.summary.close-game'),
      desc:
        resultTitle === GameResultTitle.victory
          ? t('analysis.summary.close-game-desc-win')
          : t('analysis.summary.close-game-desc-lose'),
    };
  }

  const playerMoves = game.moves.filter((move) => move.score !== undefined && move.playerId === playerId);

  const scoreSum = playerMoves.reduce((sum, move) => {
    sum += move.score!;
    return sum;
  }, 0);

  const averagePlayerScore = scoreSum / playerMoves.length;

  if (resultTitle === GameResultTitle.victory) {
    if (averagePlayerScore > 7) {
      return { title: t('analysis.summary.easy-win'), desc: t('analysis.summary.easy-win-desc') };
    }
    if (averagePlayerScore > 3) {
      return { title: t('analysis.summary.resouding-win'), desc: t('analysis.summary.resouding-win-desc') };
    }
    if (averagePlayerScore > -3) {
      return { title: t('analysis.summary.close-game'), desc: t('analysis.summary.close-game-desc-win') };
    }
    if (averagePlayerScore > -7) {
      return { title: t('analysis.summary.tough-game'), desc: t('analysis.summary.tough-game-desc-win') };
    }
    return { title: t('analysis.summary.last-gasp-win'), desc: t('analysis.summary.last-gasp-win-desc') };
  }

  if (resultTitle === GameResultTitle.defeat) {
    if (averagePlayerScore > 7) {
      return { title: t('analysis.summary.absurd-defeat'), desc: t('analysis.summary.absurd-defeat-desc') };
    }
    if (averagePlayerScore > 3) {
      return { title: t('analysis.summary.plot-twist'), desc: t('analysis.summary.plot-twist-desc') };
    }
    if (averagePlayerScore > -3) {
      return { title: t('analysis.summary.close-game'), desc: t('analysis.summary.close-game-desc-lose') };
    }
    if (averagePlayerScore > -7) {
      return { title: t('analysis.summary.tough-game'), desc: t('analysis.summary.tough-game-desc-lose') };
    }
    return { title: t('analysis.summary.crushing-defeat'), desc: t('analysis.summary.crushing-defeat-desc') };
  }

  if ([GameResultTitle.stalemate, GameResultTitle.draw].includes(resultTitle)) {
    if (averagePlayerScore > 7) {
      return { title: t('analysis.summary.unexpected-draw'), desc: t('analysis.summary.unexpected-draw-desc') };
    }
    if (averagePlayerScore > 3) {
      return {
        title: t('analysis.summary.controversial-result'),
        desc: t('analysis.summary.controversial-result-desc'),
      };
    }
    if (averagePlayerScore > -3) {
      return { title: t('analysis.summary.close-game'), desc: t('analysis.summary.close-game-desc-draw') };
    }
    if (averagePlayerScore > -7) {
      return { title: t('analysis.summary.tough-game'), desc: t('analysis.summary.tough-game-desc-draw') };
    }
    return { title: t('analysis.summary.close-to-victory'), desc: t('analysis.summary.close-to-victory-desc') };
  }
};

const ceilingCentipawns = (cp: number) => {
  if (cp > 1000) {
    return 1000;
  }

  if (cp < -1000) {
    return -1000;
  }

  return cp;
};

// source - https://lichess.org/page/accuracy
export const getPlayersAccuracy = (game: AnalysisGame) => {
  if (game.moves.some((move) => typeof move.eval === 'undefined')) {
    return { black: undefined, white: undefined };
  }

  // from lichess source code
  const centipawns = (move: ProcessedAnalyzedMove) => {
    const cp = move.eval!.cp || 0;
    const mate = move.eval!.mate || 0;

    return move.eval?.cp !== undefined ? ceilingCentipawns(cp) : mateToCp(mate);
  };

  const win = (centipawns: number) => 50 + 50 * (2 / (1 + Math.exp(-0.00368208 * centipawns)) - 1);

  const accuracy = (winPercentBefore: number, winPercentAfter: number) => {
    if (winPercentAfter >= winPercentBefore) {
      return 100;
    }

    return (
      103.1668100711649 * Math.exp(-0.04354415386753951 * (winPercentBefore - winPercentAfter)) - 3.166924740191411
    );
  };

  const round = (value: number) => Math.min(Math.trunc(value), 100);

  let blackMovesCount = 0;
  let whiteMovesCount = 0;

  const acc = game.moves.reduce(
    (acc, move, index, moves) => {
      const isWhite = move.playerId === game.whitePlayerId;
      const prevMove = moves[index - 1];

      const whiteWinPercentBefore = prevMove ? win(centipawns(prevMove)) : 50;
      const whiteWinPercentAfter = win(centipawns(move));

      if (isWhite) {
        const accuracyPercent = accuracy(whiteWinPercentBefore, whiteWinPercentAfter);

        acc.white += accuracyPercent;
        whiteMovesCount++;
      } else {
        const blackWinPercentBefore = 100 - whiteWinPercentBefore;
        const blackWinPercentAfter = 100 - whiteWinPercentAfter;
        const accuracyPercent = accuracy(blackWinPercentBefore, blackWinPercentAfter);

        acc.black += accuracyPercent;
        blackMovesCount++;
      }

      return acc;
    },
    { black: 0, white: 0 }
  );

  return {
    black: blackMovesCount ? round(acc.black / blackMovesCount) : 0,
    white: whiteMovesCount ? round(acc.white / whiteMovesCount) : 0,
  };
};

export const processContextfullAnalyzedMove = ({
  move,
  game,
  cgApi,
}: {
  move: ContextfullAnalyzedMove;
  game: AnalysisGame;
  cgApi: Api;
}) => {
  const moveColor = move.index % 2 === 0 ? 'w' : 'b';
  const previousMove = game?.moves[move.index - 1];
  const isBestMove = move.best.move === move.current.move;

  const best = {
    fullMove: move.best.move,
    san: cgApi.convertToSan({ move: move.best.move as `${Key}${Key}`, fen: move.fen }),
    score: previousMove ? previousMove.score! : evaluateScore(move.best.score, moveColor),
    eval: previousMove ? previousMove.eval! : parseEval(move.best, moveColor),
  };

  const evaluated = {
    score: isBestMove ? best.score : evaluateScore(move.current.score, moveColor),
    move: move.current.move,
    san: cgApi.convertToSan({ move: move.current.move as `${Key}${Key}`, fen: move.fen }),
  };

  return {
    eval: isBestMove ? best.eval : parseEval(move.current, moveColor),
    score: evaluated.score,
    best,
    grade: gradeMove(evaluated, moveColor, best),
  };
};

export const buildAnalyzedMovesKey = (id: string) => `${ANALYZED_MOVES_KEY}-${id}`;

export const removeIrrelevantAnalyzes = (games: Play[]) => {
  const storedKeys = Object.keys(Storage.getItems());
  storedKeys.forEach((key) => {
    if (new RegExp(ANALYZED_MOVES_KEY).test(key)) {
      const id = key.replace(`${ANALYZED_MOVES_KEY}-`, '');

      if (!games.some((game) => game.id === id)) {
        Storage.removeItem(key);
      }
    }
  });
};
